import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "../layout/Container";
import { ASSETS } from "../../constants/assets";
import { SITE_CONFIG } from "../../config/site";

const QUICK_LINK_KEYS: { key: string; to: string }[] = [
  { key: "nav.home", to: "/" },
  { key: "nav.about", to: "/about-us" },
  { key: "nav.services", to: "/services" },
  { key: "nav.team", to: "/team" },
  { key: "nav.contact", to: "/contact" },
];

export function Footer(): React.ReactElement {
  const { t } = useTranslation();

  return (
    <footer
      id="contact"
      className="bg-navy text-white overflow-hidden"
      role="contentinfo"
    >
      <Container size="full" className="py-12 md:py-16 flex flex-col items-center">
        <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-12 md:gap-8 text-center justify-items-center">
          <div className="flex flex-col gap-4 items-center max-w-[324px]">
            <img
              src={ASSETS.logoWhite}
              alt="BalkanFinXpert"
              className="h-[50px] w-auto object-contain"
            />
            <p className="text-white/85 text-base font-medium leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center pb-4">
              <h3 className="text-[#f8f8f8] text-2xl font-semibold tracking-tight">
                {t("footer.quickLinks")}
              </h3>
              <div className="pt-2 px-2" aria-hidden>
                <div className="h-0.5 w-16 bg-gold rounded" />
              </div>
            </div>
            <nav aria-label={t("nav.home")}>
              <ul className="flex flex-col gap-4 items-center">
                {QUICK_LINK_KEYS.map(({ key, to }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-[#f8f8f8] text-xl font-medium hover:underline"
                    >
                      {t(key)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="flex flex-col items-center max-w-[324px]">
            <div className="flex flex-col items-center pb-4">
              <h3 className="text-[#f8f8f8] text-2xl font-semibold tracking-tight">
                {t("footer.contacts")}
              </h3>
              <div className="pt-2 px-2" aria-hidden>
                <div className="h-0.5 w-16 bg-gold rounded" />
              </div>
            </div>
            <div className="flex flex-col gap-4 items-center">
            <div className="flex items-center justify-center gap-4">
              <img
                src={ASSETS.iconEmail}
                alt=""
                className="w-7 h-7 object-contain shrink-0"
                aria-hidden
              />
              <a
                href="mailto:office@balkanfin.com"
                className="text-base font-medium hover:underline"
              >
                office@balkanfin.com
              </a>
            </div>
            <div className="flex items-center justify-center gap-4">
              <img
                src={ASSETS.iconPhone}
                alt=""
                className="w-7 h-7 object-contain shrink-0"
                aria-hidden
              />
              <span className="text-base font-medium">{SITE_CONFIG.phone}</span>
            </div>
            <div className="flex items-center justify-center gap-4 text-center">
              <img
                src={ASSETS.iconLocation}
                alt=""
                className="w-7 h-7 object-contain shrink-0"
                aria-hidden
              />
              <span className="text-base font-medium leading-relaxed">
                {t("footer.address")}
              </span>
            </div>
            </div>
          </div>
        </div>

        <p className="mt-8 text-white/80 text-base text-center">
          {t("footer.quality")}
        </p>

        <div className="mt-8 pt-8 border-t border-white/20 flex flex-wrap justify-center gap-4 text-sm">
          <button type="button" className="text-white/80 hover:underline">
            {t("footer.privacy")}
          </button>
          <span className="text-white/60">|</span>
          <button type="button" className="text-white/80 hover:underline">
            {t("footer.cookies")}
          </button>
        </div>

        <p className="mt-6 text-white/50 text-base text-center">
          {t("footer.copyright")}
        </p>
      </Container>
    </footer>
  );
}

export default Footer;
